import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BookDemoProvider } from "@/context/BookDemoContext";
import { VideoModalProvider } from "@/context/VideoModalContext";
import { IndustryModalProvider } from "@/context/IndustryModalContext";
import { CartProvider } from "@/context/CartContext";
import BookDemoModal from "@/components/site/BookDemoModal";
import VideoModal from "@/components/site/VideoModal";
import IndustryModal from "@/components/site/IndustryModal";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ProtectedRoute from "@/components/dashboard/ProtectedRoute";
import SubscriptionGuard from "@/components/dashboard/SubscriptionGuard";
import Home from "@/pages/Home";
import Fordele from "@/pages/Fordele";
import Funktioner from "@/pages/Funktioner";
import Brancher from "@/pages/Brancher";
import Priser from "@/pages/Priser";
import Kontakt from "@/pages/Kontakt";
import Login from "@/pages/Login";
import OpretKonto from "@/pages/OpretKonto";
import GlemtAdgangskode from "@/pages/GlemtAdgangskode";
import NulstilAdgangskode from "@/pages/NulstilAdgangskode";
import AcceptInvite from "@/pages/AcceptInvite";
import BillingSuccess from "@/pages/BillingSuccess";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminKunder from "@/pages/admin/AdminKunder";
import AdminAiBrancher from "@/pages/admin/AdminAiBrancher";
import AdminLogin from "@/pages/admin/AdminLogin";
import Admin2FA from "@/pages/admin/Admin2FA";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import EmployeeLayout from "@/pages/employee/EmployeeLayout";
import Overview from "@/pages/dashboard/Overview";
import Jobs from "@/pages/dashboard/Jobs";
import Customers from "@/pages/dashboard/Customers";
import Employees from "@/pages/dashboard/Employees";
import Materials from "@/pages/dashboard/Materials";
import OrderMode from "@/pages/dashboard/OrderMode";
import DeliveryChecklist from "@/pages/dashboard/DeliveryChecklist";
import FakturaKlar from "@/pages/dashboard/FakturaKlar";
import FakturaTemplateEditor from "@/pages/dashboard/FakturaTemplateEditor";
import Settings from "@/pages/dashboard/Settings";
import AuthCallback from "@/pages/AuthCallback";
import EmployeeSystem from "@/pages/EmployeeSystem";
import CustomerPortal from "@/pages/CustomerPortal";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
    return null;
}

/**
 * AuthGate — synchronously checks the URL hash for a session_id (Emergent Google Auth
 * redirect) BEFORE any protected route can run its auth check. This prevents the
 * AuthProvider from redirecting to /login while the callback is exchanging the session.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AuthGate({ children }) {
    const location = useLocation();
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return children;
}

function PublicLayout({ children }) {
    return (
        <>
            <Header />
            <main className="pt-20 sm:pt-24">{children}</main>
            <Footer />
            <BookDemoModal />
            <VideoModal />
            <IndustryModal />
        </>
    );
}

function App() {
    return (
        <div className="App font-sans">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <BookDemoProvider>
                            <VideoModalProvider>
                                <IndustryModalProvider>
                                    <ScrollToTop />
                                    <AuthGate>
                                    <Routes>
                                        {/* Public site */}
                                        <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                                        <Route path="/fordele" element={<PublicLayout><Fordele /></PublicLayout>} />
                                        <Route path="/funktioner" element={<PublicLayout><Funktioner /></PublicLayout>} />
                                        <Route path="/brancher" element={<PublicLayout><Brancher /></PublicLayout>} />
                                        <Route path="/priser" element={<PublicLayout><Priser /></PublicLayout>} />
                                        <Route path="/kontakt" element={<PublicLayout><Kontakt /></PublicLayout>} />

                                        {/* Auth flows */}
                                        <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
                                        <Route path="/opret-konto" element={<PublicLayout><OpretKonto /></PublicLayout>} />
                                        <Route path="/glemt-adgangskode" element={<PublicLayout><GlemtAdgangskode /></PublicLayout>} />
                                        <Route path="/nulstil-adgangskode" element={<PublicLayout><NulstilAdgangskode /></PublicLayout>} />
                                        <Route path="/reset-password" element={<PublicLayout><NulstilAdgangskode /></PublicLayout>} />
                                        <Route path="/forgot-password" element={<PublicLayout><GlemtAdgangskode /></PublicLayout>} />
                                        <Route path="/accept-invite/:token" element={<PublicLayout><AcceptInvite /></PublicLayout>} />
                                        <Route path="/billing/success" element={<ProtectedRoute><PublicLayout><BillingSuccess /></PublicLayout></ProtectedRoute>} />

                                        {/* Dashboard (protected) */}
                                        <Route path="/dashboard" element={<ProtectedRoute><SubscriptionGuard><DashboardLayout /></SubscriptionGuard></ProtectedRoute>}>
                                            <Route index element={<Overview />} />
                                            <Route path="jobs" element={<Jobs />} />
                                            <Route path="customers" element={<Customers />} />
                                            <Route path="employees" element={<Employees />} />
                                            <Route path="materials" element={<Materials />} />
                                            <Route path="indkoebsliste" element={<OrderMode />} />
                                            <Route path="leveringscheckliste" element={<DeliveryChecklist />} />
                                            <Route path="faktura-klar" element={<FakturaKlar />} />
                                            <Route path="faktura-klar/template" element={<FakturaTemplateEditor />} />
                                            <Route path="settings" element={<Settings />} />
                                        </Route>

                                        {/* Separate employee login; never replaces the administrator session. */}
                                        <Route path="/system/medarbejder" element={<EmployeeSystem />} />
                                        <Route path="/demo" element={<Navigate to="/system/medarbejder" replace />} />
                                        <Route path="/kunde/:token" element={<CustomerPortal />} />

                                        {/* Medarbejder — mobil-først del (state-baseret navigation) */}
                                        <Route path="/medarbejder/*" element={<ProtectedRoute><EmployeeLayout /></ProtectedRoute>} />

                                        {/* Admin / Owner console (super_admin only) */}
                                        <Route path="/admin/login" element={<AdminLogin />} />
                                        <Route path="/admin" element={<AdminLayout />}>
                                            <Route index element={<AdminOverview />} />
                                            <Route path="2fa" element={<Admin2FA />} />
                                            <Route path="overview" element={<AdminOverview />} />
                                            <Route path="leads" element={<AdminLeads />} />
                                            <Route path="kunde" element={<AdminKunder />} />
                                            <Route path="kunder" element={<AdminKunder />} />
                                            <Route path="ai-brancher" element={<AdminAiBrancher />} />
                                            <Route path="planning" element={<div className="p-12 text-center text-slate-400">Planlægning — kommer snart</div>} />
                                            <Route path="payments" element={<div className="p-12 text-center text-slate-400">Betalinger — kommer snart</div>} />
                                            <Route path="customers" element={<div className="p-12 text-center text-slate-400">Kunder — kommer snart</div>} />
                                        </Route>
                                    </Routes>
                                    </AuthGate>
                                    <Toaster position="top-right" richColors />
                                </IndustryModalProvider>
                            </VideoModalProvider>
                        </BookDemoProvider>
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
